// src/tenantAdmin/components/CorrectEmailDialog.jsx
//
// "Correggi l'indirizzo email" — fix a mistyped sign-in address.
//
// ⭐⭐ API-AGNOSTIC BY CONTRACT. It knows no route, imports no API module and
// takes no ids: the caller hands it `onSubmit(email)`, which resolves with the
// server's `{previous_email, email}` or throws. That is what lets the SAME dialog
// serve the workspace route (PATCH /tenant-admin/users/{uid}/email) and the
// company one (PATCH /admin/customers/{cid}/users/{uid}/email), whose refusals
// and shape are identical while their paths are not.
//
// ⚠️ The correction is SILENT: the backend sends no mail to either address, so
// the person cannot learn their new sign-in address from the product. The hint
// says the admin has to tell them — it is the whole reason this is a dialog and
// not an inline field. For an INVITED person (`invited`, which only the caller
// can know) the hint speaks of the invitation instead: they have never signed in,
// and the correction kills the link sent to the old address.
//
// ⛔ Errors are worded here, membershipRefusalCopy FIRST (this route answers the
// two "belongs elsewhere" 409s, whose remedy is per-action) and userActionErrorCopy
// second. ⛔ Never `err.message`, never the server `warning`.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from '../../admin/components/AdminModal.jsx';
import { membershipRefusalCopy } from '../membershipRefusal.js';
import { userActionErrorCopy } from '../userActionError.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const fieldLabel = {
  display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px',
  textTransform: 'uppercase', color: COLORS.textSecondary, fontFamily: FONT_SANS,
  marginBottom: '8px',
};
const field = {
  width: '100%', height: '46px', boxSizing: 'border-box',
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.control, color: COLORS.ink, fontSize: '14px',
  fontFamily: FONT_SANS, outline: 'none', padding: '0 14px',
};

export default function CorrectEmailDialog({ open, currentEmail, invited = false, onSubmit, onClose }) {
  const { t } = useTranslation('admin');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Seeded with the WRONG address on purpose: a correction is usually a typo, so
  // the admin edits rather than retypes.
  useEffect(() => {
    if (!open) return;
    setEmail(currentEmail || '');
    setError('');
    setLoading(false);
  }, [open, currentEmail]);

  const next = email.trim();
  const dirty = next !== '' && next !== (currentEmail || '');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!dirty || loading) return;
    setError('');
    setLoading(true);
    try {
      await onSubmit(next);
      // The caller owns the outcome notice (it knows whether the row was still
      // invited); the dialog's only job on success is to get out of the way.
      onClose();
    } catch (err) {
      setError(membershipRefusalCopy(t, err, 'email') || userActionErrorCopy(t, err));
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    if (loading) return; // a write is in flight; its outcome still has to land
    onClose();
  }

  return (
    <AdminModal open={open} onClose={handleClose} title={t('manage.correctEmailTitle')} width={460}>
      <form onSubmit={handleSubmit}>
        <label htmlFor="ced-email" style={fieldLabel}>{t('shared.email')}</label>
        <input
          id="ced-email"
          type="email"
          value={email}
          autoComplete="off"
          onChange={e => setEmail(e.target.value)}
          style={field}
        />
        <p style={{
          margin: '8px 0 0', fontSize: '11px', lineHeight: 1.5,
          color: COLORS.textTertiary, fontFamily: FONT_SANS,
        }}>
          {t(invited ? 'manage.correctEmailHintInvited' : 'manage.correctEmailHint')}
        </p>

        {error && (
          <div role="alert" style={{
            marginTop: '16px', padding: '10px 14px', borderRadius: RADII.controlInner,
            background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
            color: SEMANTIC.errore.fg, fontSize: '13px', fontFamily: FONT_SANS,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            style={{
              height: '40px', padding: '0 18px', borderRadius: RADII.controlInner,
              border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
              color: COLORS.ink, fontSize: '13px', fontFamily: FONT_SANS,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1,
            }}
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading || !dirty}
            style={{
              height: '40px', padding: '0 18px', borderRadius: RADII.controlInner, border: 'none',
              background: (loading || !dirty) ? COLORS.borderStrong : COLORS.accent,
              color: COLORS.surface, fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
              cursor: (loading || !dirty) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('profile.saving') : t('common:actions.save')}
          </button>
        </div>
      </form>
    </AdminModal>
  );
}
